import * as THREE from 'three';
import { hexDist, hexRing, hexToWorld, type Hex, type MapDef } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { Kit, place } from '../seasons/kit.ts';
import { looksRng, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The forest road at night (Caras prestadas 3, docs/SAGAS.md): the clearing walled in by dark pines and dead
 * trunks, low mist between them, a cold moon. Out at the pit (`pit`, the sim's): a hole choked with roots, earth
 * thrown up round it, and beside it Ebbe's wool cart, its shaft on the ground and no oxen, and a long shape under a
 * grey sheet. Nothing gory: a hat on the sheet, a boot showing.
 *
 * Cheap like the village: the whole wood is one merged mesh (vertex colours), mist and moonlight one glow layer.
 * No lights are added. Decoration only.
 */

const PINE = ['#1c2a26', '#1a2622', '#22302a', '#18221f'];
const BARK = ['#2a221d', '#241e1a', '#2f2620'];
const DEAD = ['#4a4640', '#3e3a35'];
const EARTH = ['#3a2f26', '#33291f', '#2d241c'];
const WOOL = ['#d9dad6', '#cbccc8', '#bfc0bc'];
const MIST = new THREE.Color('#8ea0c4');
const MOON = new THREE.Color('#c8d6f4');

const P = (() => {
  const cone = new THREE.ConeGeometry(0.5, 1, 7);
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 7);
  const box = new THREE.BoxGeometry(1, 1, 1);
  const rock = new THREE.DodecahedronGeometry(0.5, 0);
  const wheel = new THREE.CylinderGeometry(0.5, 0.5, 0.12, 10).rotateZ(Math.PI / 2);
  const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.02, 16);
  const arc = new THREE.TorusGeometry(0.5, 0.06, 5, 10, Math.PI);
  const blob = new THREE.SphereGeometry(0.5, 9, 6);
  return { cone, cyl, box, rock, wheel, disc, arc, blob };
})();

const pick = <T>(list: T[], r: number): T => list[Math.floor(r * list.length) % list.length] as T;

export const buildWoods = (ctx: OmenCtx, map: MapDef, pit: { x: number; y: number } | null): OmenScene => {
  const group = new THREE.Group();
  group.name = 'woods';
  const low = ctx.low;
  const rnd = looksRng(0x3a0d5 + map.radius * 17);
  const body = new Kit();
  const road: Hex[] = map.sectors.flatMap((s) => s.path);
  const pitHex = pit ? nearestHex(pit) : null;
  // A glade round the pit (two hexes clear, thinner at three) and a way to it from the clearing: it must read from above.
  const toPit = map.trail ?? (pitHex ? lineHexes({ q: 0, r: 0 }, pitHex) : []);
  const clear = (h: Hex): boolean => !road.some((r) => hexDist(r, h) <= 0) && (!pitHex || hexDist(pitHex, h) > 2) && !toPit.some((x) => hexDist(x, h) <= 0);
  const world = (h: Hex): THREE.Vector3 => {
    const w = hexToWorld(h.q, h.r);
    const p = ctx.at(w.x + (rnd() - 0.5) * 600, w.y + (rnd() - 0.5) * 600, 0);
    p.y = ctx.ground(p.x, p.z);
    return p;
  };

  /** A pine: a dark trunk and three stacked cones, a little crooked. */
  const pine = (p: THREE.Vector3, s: number): void => {
    const m = place(p.x, p.y, p.z, rnd() * TAU, s, (rnd() - 0.5) * 0.08, (rnd() - 0.5) * 0.08);
    body.add(P.cyl, pick(BARK, rnd()), m, place(0, 0.35, 0, 0, new THREE.Vector3(0.12, 0.7, 0.12)));
    for (let k = 0; k < 3; k++) body.add(P.cone, pick(PINE, rnd()), m, place(0, 0.75 + k * 0.42, 0, rnd() * TAU, new THREE.Vector3(1.05 - k * 0.28, 0.8, 1.05 - k * 0.28)));
  };
  /** A dead tree: a pale trunk and two bare branches. */
  const snag = (p: THREE.Vector3, s: number): void => {
    const m = place(p.x, p.y, p.z, rnd() * TAU, s);
    const c = pick(DEAD, rnd());
    body.add(P.cyl, c, m, place(0, 0.8, 0, 0, new THREE.Vector3(0.11, 1.6, 0.11), 0.05));
    body.add(P.cyl, c, m, place(0.18, 1.25, 0, 0, new THREE.Vector3(0.05, 0.6, 0.05), 0, -0.9));
    body.add(P.cyl, c, m, place(-0.14, 1.05, 0.05, 0.4, new THREE.Vector3(0.04, 0.45, 0.04), 0, 0.8));
  };

  // The wood: dense from the clearing's edge out past the board, thickest farther out.
  const inner = 5;
  for (let r = inner; r <= map.radius + 3; r++) {
    for (const h of hexRing(r)) {
      if (!clear(h)) continue;
      const nearPit = pitHex ? hexDist(pitHex, h) === 3 : false;
      const keep = (r === inner ? 0.35 : r === inner + 1 ? 0.6 : 0.85) * (nearPit ? 0.4 : 1);
      if (rnd() > keep * (low ? 0.6 : 1)) continue;
      const p = world(h);
      if (rnd() < 0.12) snag(p, 0.9 + rnd() * 0.4);
      else pine(p, 0.9 + rnd() * 0.6 + (r - inner) * 0.05);
    }
  }

  // The pit, the cart and Ebbe under the sheet.
  let pitTop: THREE.Vector3 | null = null;
  if (pit) {
    const c = ctx.at(pit.x, pit.y, 0);
    c.y = ctx.ground(c.x, c.z);
    pitTop = c.clone();
    const m = place(c.x, c.y, c.z, rnd() * TAU);
    // The glade's floor: trodden earth, dark, so the hole in it is darker still.
    body.add(P.disc, '#2b241e', m, place(0, 0.012, 0, 0, new THREE.Vector3(3.0, 1, 3.0)));
    // A mound of earth thrown up all round it: a ring of clods, rising toward the lip.
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * TAU + rnd() * 0.2;
      const d = 0.78 + rnd() * 0.12;
      body.add(P.blob, pick(EARTH, rnd()), m, place(Math.cos(a) * d, 0.05, Math.sin(a) * d, a, new THREE.Vector3(0.42, 0.22 + rnd() * 0.1, 0.3)));
    }
    for (let i = 0; i < 8; i++) {
      const a = rnd() * TAU;
      const d = 1.05 + rnd() * 0.35;
      body.add(P.rock, pick(EARTH, rnd()), m, place(Math.cos(a) * d, 0.04, Math.sin(a) * d, rnd() * TAU, new THREE.Vector3(0.2, 0.1, 0.16)));
    }
    // The hole: black at the middle, brown at the lip, going down (a gradient of discs and its earthen wall).
    body.add(P.disc, '#21170f', m, place(0, 0.04, 0, 0, new THREE.Vector3(1.55, 1, 1.55)));
    body.add(P.disc, '#110b07', m, place(0, 0.05, 0, 0, new THREE.Vector3(1.25, 1, 1.25)));
    body.add(P.disc, '#050304', m, place(0, 0.06, 0, 0, new THREE.Vector3(0.95, 1, 0.95)));
    body.add(P.cyl, '#0c0806', m, place(0, -0.35, 0, 0, new THREE.Vector3(1.2, 0.8, 1.2)));
    // Big gnarled roots arching over the hole, dark, and a few thick ones lying across it.
    const ROOTS = ['#4a3627', '#3f2e21', '#553d2a'];
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * TAU + rnd() * 0.35;
      const w = 0.95 + rnd() * 0.45;
      body.add(P.arc, pick(ROOTS, rnd()), m, place(Math.cos(a) * 0.12, 0.02, Math.sin(a) * 0.12, a + Math.PI / 2, new THREE.Vector3(w, 0.9 + rnd() * 0.45, 2.2 + rnd() * 0.8), (rnd() - 0.5) * 0.3));
    }
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI + rnd() * 0.5;
      body.add(P.cyl, pick(ROOTS, rnd()), m, place(0, 0.14 + i * 0.05, 0, a, new THREE.Vector3(0.11, 1.9 + rnd() * 0.4, 0.11), Math.PI / 2 + (rnd() - 0.5) * 0.25));
    }
    // Roots climbing out over the lip and down into the dark.
    for (let i = 0; i < 9; i++) {
      const a = rnd() * TAU;
      body.add(P.cone, pick(ROOTS, rnd()), m, place(Math.cos(a) * 0.62, 0.02, Math.sin(a) * 0.62, a, new THREE.Vector3(0.09, 0.7, 0.09), Math.PI / 2 - 0.35));
    }
    // Ebbe's cart: a wool wagon beside the pit, tipped on a broken wheel, shaft down, no oxen, bales spilt.
    const side = rnd() < 0.5 ? 1 : -1;
    const cm = place(c.x + side * 1.5, c.y, c.z + 0.5, side * 0.6 + 0.3, 1.25, 0, side * 0.22);
    body.add(P.box, '#4a3628', cm, place(0, 0.3, 0, 0, new THREE.Vector3(0.55, 0.07, 0.9)));
    for (const x of [-0.27, 0.27]) body.add(P.box, '#3b2c22', cm, place(x, 0.42, 0, 0, new THREE.Vector3(0.04, 0.2, 0.9)));
    for (const z of [-0.43, 0.43]) body.add(P.box, '#3b2c22', cm, place(0, 0.42, z, 0, new THREE.Vector3(0.55, 0.2, 0.04)));
    body.add(P.wheel, '#2c2620', cm, place(-0.33, 0.2, 0.1, 0, new THREE.Vector3(0.42, 0.42, 0.42)));
    body.add(P.box, '#3b2c22', cm, place(0, 0.1, 0.75, 0, new THREE.Vector3(0.05, 0.05, 0.6), 0.35));
    // Wool bales piled high, pale so they read from above.
    for (let k = 0; k < 6; k++) body.add(P.blob, pick(WOOL, rnd()), cm, place((k % 2) * 0.24 - 0.12, 0.5 + Math.floor(k / 2) * 0.12, -0.28 + (k % 3) * 0.24, rnd() * TAU, new THREE.Vector3(0.3, 0.2, 0.3)));
    // The broken wheel flat on the ground, and bales spilt in the road.
    body.add(P.wheel, '#2c2620', place(c.x + side * 2.25, c.y + 0.03, c.z + 0.2, 0.4, new THREE.Vector3(0.5, 0.5, 0.5), 0, Math.PI / 2));
    for (let k = 0; k < 3; k++) body.add(P.blob, pick(WOOL, rnd()), place(c.x + side * (2.0 + rnd() * 0.5), c.y + 0.1, c.z + 1.1 + rnd() * 0.6, rnd() * TAU, new THREE.Vector3(0.32, 0.2, 0.32)));
    // Ebbe: a long low shape under a grey sheet by the cart, his hat on it, a boot showing.
    const em = place(c.x + side * 1.2, c.y, c.z - 0.95, side * 1.2);
    body.add(P.blob, '#7d808a', em, place(0, 0.08, 0, 0, new THREE.Vector3(0.36, 0.16, 0.95)));
    body.add(P.blob, '#7d808a', em, place(0, 0.1, 0.32, 0, new THREE.Vector3(0.3, 0.17, 0.28)));
    body.add(P.box, '#2a201a', em, place(0.08, 0.06, -0.52, 0, new THREE.Vector3(0.09, 0.1, 0.16)));
    body.add(P.cyl, '#3a3028', em, place(-0.05, 0.2, 0.1, 0.3, new THREE.Vector3(0.28, 0.03, 0.28)));
    body.add(P.cyl, '#3a3028', em, place(-0.05, 0.25, 0.1, 0.3, new THREE.Vector3(0.15, 0.08, 0.15)));
  }

  // Stepping stones from the clearing to the glade.
  for (const h of toPit) {
    const w = hexToWorld(h.q, h.r);
    for (let k = 0; k < 3; k++) {
      const p = ctx.at(w.x + (rnd() - 0.5) * 700, w.y + (rnd() - 0.5) * 700, 0);
      p.y = ctx.ground(p.x, p.z);
      body.add(P.rock, '#6a6660', place(p.x, p.y, p.z, rnd() * TAU, new THREE.Vector3(0.22, 0.05, 0.18)));
    }
  }

  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, flatShading: true });
  const mesh = body.build(mat, 'woods-body');
  if (mesh) {
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  // Mist low between the trunks, and moonlight falling into the clearing: one glow layer.
  const glow = new GlowLayer(low ? 48 : 120);
  group.add(glow.mesh);
  const mists = Array.from({ length: low ? 18 : 46 }, () => {
    const ring = hexRing(inner - 1 + Math.floor(rnd() * 5));
    const h = ring[Math.floor(rnd() * ring.length)] as Hex;
    const w = hexToWorld(h.q, h.r);
    const p = ctx.at(w.x, w.y, 0.25 + rnd() * 0.3);
    return { p, size: 2.2 + rnd() * 2.2, ph: rnd() * TAU, drift: 0.1 + rnd() * 0.2 };
  });
  const shafts = Array.from({ length: low ? 3 : 6 }, () => {
    const a = rnd() * TAU;
    const d = 1500 + rnd() * 2500;
    return { p: ctx.at(Math.cos(a) * d, Math.sin(a) * d, 0.05), ph: rnd() * TAU };
  });

  const sky = new SkyLook(ctx, {
    hemi: ['#4a5878', '#121820', 0.55],
    key: ['#9fb4dc', 0.5],
    rim: ['#7f98cc', 1.05],
    background: '#0a0f1a',
    fog: '#111826',
    mist: { low: '#151c2a', mid: '#10161f', far: '#090d14', alpha: 1.15 },
  });

  return {
    group,
    frame: (t: number) => {
      sky.pulse({});
      glow.begin();
      for (const m of mists) glow.add(m.p.x + Math.sin(t * m.drift + m.ph) * 0.4, m.p.y, m.p.z + Math.cos(t * m.drift * 0.7 + m.ph) * 0.3, MIST, m.size, 0.05 + 0.02 * Math.sin(t * 0.5 + m.ph));
      for (const s of shafts) glow.add(s.p.x, s.p.y, s.p.z, MOON, 2.6, 0.06 + 0.02 * Math.sin(t * 0.3 + s.ph));
      // A faint cold breath over the pit: something is down there.
      if (pitTop) {
        // Only a thin cold breath rising from it (the hole itself stays dark).
        glow.add(pitTop.x, pitTop.y + 0.6, pitTop.z, MIST, 0.9, 0.05 + 0.03 * Math.sin(t * 1.3));
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      mesh?.geometry.dispose();
      mat.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};

/** The hex a sim point lies in (rounded through world space, close enough for keeping trees off it). */
const nearestHex = (p: { x: number; y: number }): Hex => {
  let best: Hex = { q: 0, r: 0 };
  let bd = Infinity;
  for (let r = 0; r <= 9; r++)
    for (const h of hexRing(r)) {
      const w = hexToWorld(h.q, h.r);
      const d = (w.x - p.x) ** 2 + (w.y - p.y) ** 2;
      if (d < bd) {
        bd = d;
        best = h;
      }
    }
  return best;
};

/** The hexes on a straight line between two (inclusive), for the way to the pit. */
const lineHexes = (a: Hex, b: Hex): Hex[] => {
  const n = hexDist(a, b);
  const out: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    const k = n ? i / n : 0;
    const q = a.q + (b.q - a.q) * k;
    const r = a.r + (b.r - a.r) * k;
    const s = -q - r;
    let rq = Math.round(q), rr = Math.round(r);
    const rs = Math.round(s);
    const dq = Math.abs(rq - q), dr = Math.abs(rr - r), ds = Math.abs(rs - s);
    if (dq > dr && dq > ds) rq = -rr - rs;
    else if (dr > ds) rr = -rq - rs;
    out.push({ q: rq, r: rr });
  }
  return out;
};
